import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon } from 'lucide-react';
import { getProgram, programs } from '../data/programs';
import { Button } from '../components/ui/Button';
import { Eyebrow } from '../components/ui/Eyebrow';
import { CtaBand } from '../components/ui/CtaBand';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
export function ProgramDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const program = slug ? getProgram(slug) : undefined;
  useDocumentMeta({
    title: program ? `${program.name} — Republic` : 'Programs — Republic',
    description: program ? program.summary : 'Hormone, metabolic, longevity and everyday care from Republic.',
    image: program?.image
  });
  if (!program) {
    return <main className="bg-cream" data-id="element-515" data-mp="vf264smcgoms">
        <div className="mx-auto max-w-shell px-6 py-32 md:px-8" data-id="element-516" data-mp="vesc8ty6hxbt">
          <Eyebrow className="text-brick" data-id="element-517" data-mp="107v9pd62tqxa">Not found</Eyebrow>
          <h1 className="mt-4 max-w-2xl font-serif text-4xl leading-tight text-ink md:text-6xl" data-id="element-518" data-mp="1z21im4tqstrm">
            We could not find that program.
          </h1>
          <Link to="/programs" className="mt-8 inline-flex items-center gap-2 text-sm text-brick hover:text-ink" data-id="element-519" data-mp="1v214ymegwmlm">
            <ArrowLeftIcon className="h-4 w-4" data-id="element-520" data-mp="241ecvamy1348" />
            Back to all programs
          </Link>
        </div>
      </main>;
  }
  const related = programs.filter(item => item.slug !== program.slug).slice(0, 3);
  return <main data-id="element-521" data-mp="2dpwbpmbnx058">
      <header className="bg-cream" data-id="element-522" data-mp="2dq623b0md9bg">
        <div className="mx-auto max-w-shell px-6 pt-12 md:px-8 md:pt-16" data-id="element-523" data-mp="2dtabqnwusx9i">
          <Link to="/programs" className="inline-flex items-center gap-2 text-sm text-ink-muted transition-colors duration-150 ease-republic hover:text-ink" data-id="element-524" data-mp="2rpxnhwtgtrzh">
            <ArrowLeftIcon className="h-4 w-4" data-id="element-525" data-mp="xd7qh67sw2nv" />
            All programs
          </Link>

          <div className="mt-10 grid gap-12 lg:grid-cols-[1.15fr_0.85fr] lg:gap-16" data-id="element-526" data-mp="sewfut9olo4l">
            <div data-id="element-527" data-mp="2sgw10wnb5pjf">
              <Eyebrow className="text-brick" data-id="element-528" data-mp="2shfarq2t17md">{program.category}</Eyebrow>
              <h1 className="mt-4 font-serif text-[2.75rem] leading-[1.02] text-ink md:text-[4rem]" data-id="element-529" data-mp="2xg0a7na8fn9r">
                {program.name}
              </h1>
              <p className="mt-8 max-w-xl text-lg leading-relaxed text-ink-soft" data-id="element-530" data-mp="32gekzd37xbbh">
                {program.description}
              </p>

              <dl className="rule mt-10 grid gap-6 pt-8 sm:grid-cols-3" data-id="element-531" data-mp="3bfi4uja7lur1">
                <div data-id="element-532" data-mp="3gdtcqogy1v6m">
                  <dt className="text-[0.6875rem] uppercase tracking-label text-ink-muted" data-id="element-533" data-mp="3gd1hjer9dzgv">From</dt>
                  <dd className="mt-2 font-serif text-3xl text-ink" data-id="element-534" data-mp="3l49q68krlgsz">
                    ${program.priceFrom}
                    <span className="ml-2 font-sans text-sm text-ink-muted" data-id="element-535" data-mp="3q2l2cpyj83ed">{program.cadence}</span>
                  </dd>
                </div>
                <div data-id="element-536" data-mp="3u3509vqxjb1k">
                  <dt className="text-[0.6875rem] uppercase tracking-label text-ink-muted" data-id="element-537" data-mp="2ynajs7h9iok">
                    Eligibility
                  </dt>
                  <dd className="mt-2 text-sm leading-relaxed text-ink-soft" data-id="element-538" data-mp="1niionbj78ton">
                    {program.eligibility}
                  </dd>
                </div>
                <div data-id="element-539" data-mp="1efrav52upltu">
                  <dt className="text-[0.6875rem] uppercase tracking-label text-ink-muted" data-id="element-540" data-mp="1ehath2bzc8ys">
                    First shipment
                  </dt>
                  <dd className="mt-2 text-sm leading-relaxed text-ink-soft" data-id="element-541" data-mp="267fjphmf7q7x">
                    Within 48 hours of protocol approval
                  </dd>
                </div>
              </dl>

              <div className="mt-10 flex flex-wrap gap-4" data-id="element-542" data-mp="2287uvl65fnvs">
                <Button to="/get-started" data-id="element-543" data-mp="1xdkde69kbtqp">
                  Start your intake
                  <ArrowRightIcon className="h-4 w-4" data-id="element-544" data-mp="1sezce4mhl617" />
                </Button>
                <Button to="/labs" variant="secondary" data-id="element-545" data-mp="2pvy31ikqw9ep">
                  See the lab panel
                </Button>
              </div>
            </div>

            <img src={program.image} alt={`${program.name} at Republic`} className="aspect-[4/5] w-full object-cover" data-id="element-546" data-mp="2l3epglm25e9w" />
          </div>
        </div>
      </header>

      <section className="bg-cream-light" data-id="element-547" data-mp="29lxb7swnmhz3">
        <div className="mx-auto max-w-shell px-6 py-20 md:px-8 md:py-24" data-id="element-548" data-mp="1vpk3vclu3wyc">
          <div className="grid gap-14 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20" data-id="element-549" data-mp="20nbs9wn9t0xs">
            <div data-id="element-550" data-mp="20pg7haqmdmdd">
              <h2 className="font-serif text-3xl leading-tight text-ink md:text-4xl" data-id="element-551" data-mp="2odfz0cvr9gt0">
                What the membership includes
              </h2>
              <ul className="mt-8 flex flex-col gap-4" data-id="element-552" data-mp="2se9o39kxp1nh">
                {program.includes.map(item => <li key={item} className="flex items-start gap-3" data-id="element-553" data-mp="2en4jru6mfl6t">
                    <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-brick" strokeWidth={2} data-id="element-554" data-mp="2jlft83t07toi" />
                    <span className="text-base leading-relaxed text-ink-soft" data-id="element-555" data-mp="37b0mbfivg7bi">{item}</span>
                  </li>)}
              </ul>
            </div>

            <div data-id="element-556" data-mp="3lhc1j7uq1rqg">
              <h2 className="font-serif text-3xl leading-tight text-ink md:text-4xl" data-id="element-557" data-mp="3lgsjzs8zh5cy">
                How the protocol runs
              </h2>
              <ol className="mt-8" data-id="element-558" data-mp="3hfqjz978sp9x">
                {program.protocol.map((phase, index) => <li key={phase.title} className="rule grid gap-2 py-6 sm:grid-cols-[3rem_1fr]" data-id="element-559" data-mp="3v6lsulhqr6di" data-mpi={index}>
                    <span className="font-serif text-2xl text-brick" data-id="element-560" data-mp="3q92frows4zn5">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <div data-id="element-561" data-mp="2yhwc4yhuojt4">
                      <h3 className="font-serif text-xl text-ink" data-id="element-562" data-mp="2yifs49nxwy4i">{phase.title}</h3>
                      <p className="mt-2 max-w-xl text-base leading-relaxed text-ink-soft" data-id="element-563" data-mp="3chxbg9igvj6h">
                        {phase.detail}
                      </p>
                    </div>
                  </li>)}
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-cream" data-id="element-564" data-mp="mqioaolxdxte">
        <div className="mx-auto max-w-shell px-6 py-20 md:px-8 md:py-24" data-id="element-565" data-mp="mtoanw366llf">
          <Eyebrow className="text-brick" data-id="element-566" data-mp="urqa6abodw4l">Medications used</Eyebrow>
          <h2 className="mt-4 max-w-2xl font-serif text-3xl leading-tight text-ink md:text-[2.75rem]" data-id="element-567" data-mp="yt3gsmmle3ck">
            Your clinician selects from these, and names the pharmacy that fills it.
          </h2>
          <ul className="mt-12" data-id="element-568" data-mp="18sauxc15i20q">
            {program.medications.map(medication => <li key={medication.name} className="rule grid gap-2 py-6 md:grid-cols-3 md:items-baseline md:gap-8" data-id="element-569" data-mp="b4sjtuk0ontk">
                <h3 className="font-serif text-2xl text-ink" data-id="element-570" data-mp="g10w906ty2x1">{medication.name}</h3>
                <p className="text-base text-ink-soft" data-id="element-571" data-mp="l15fgxz4hbqy">{medication.form}</p>
                <p className="text-base text-ink-muted" data-id="element-572" data-mp="ptyjtkqoii2i">{medication.note}</p>
              </li>)}
          </ul>
          <p className="mt-8 max-w-2xl text-sm leading-relaxed text-ink-muted" data-id="element-573" data-mp="3oix2rz2bd7it">
            Compounded medications are not FDA-approved products. They are prepared by US-licensed
            503A and 503B pharmacies and dispensed only against a clinician-written prescription.
          </p>
        </div>
      </section>

      <section className="bg-cream-light" data-id="element-574" data-mp="3gxtilkc92x3e">
        <div className="mx-auto max-w-shell px-6 py-16 md:px-8 md:py-20" data-id="element-575" data-mp="3bzjlh46ytmf5">
          <h2 className="text-[0.6875rem] uppercase tracking-label text-ink-muted" data-id="element-576" data-mp="371qcy4i6wgbp">
            Other programs
          </h2>
          <ul className="mt-8 grid gap-8 md:grid-cols-3" data-id="element-577" data-mp="330y43cd8cdfy">
            {related.map(item => <li key={item.slug} className="rule pt-6" data-id="element-578" data-mp="2y84zqplob74e">
                <Link to={`/programs/${item.slug}`} className="group flex h-full flex-col" data-id="element-579" data-mp="2ta36xcyg3tfq">
                  <h3 className="font-serif text-2xl leading-snug text-ink transition-colors duration-150 ease-republic group-hover:text-brick" data-id="element-580" data-mp="2t7qpd0wlnl37">
                    {item.name}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink-soft" data-id="element-581" data-mp="2jdqhir9xo1qs">{item.summary}</p>
                  <span className="mt-auto inline-flex items-center gap-2 pt-6 text-sm text-brick" data-id="element-582" data-mp="2fcod7w15szhy">
                    From ${item.priceFrom}
                    <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-republic group-hover:translate-x-1" data-id="element-583" data-mp="c0yrxbczxf7q" />
                  </span>
                </Link>
              </li>)}
          </ul>
        </div>
      </section>

      <CtaBand data-id="element-584" data-mp="24cakwjvp6ag" />
    </main>;
}